import { useEffect } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { HTTPError } from 'ky';

import { api } from 'lib/request/api';
import { tcEffectiveWorkspaceIdAtom } from 'components/type-curve-workspace/atoms';
import type {
  WorkspaceTask,
  WorkspaceTasksResponse,
} from 'types/type-curve-workspace-show';
import { tcWorkspaceTasksAtom } from './task-atoms';

export const WORKSPACE_TASKS_KEY = 'tc-workspace-tasks';

const EMPTY_TASKS: WorkspaceTasksResponse = {
  tasks: [],
  completed: 0,
  total: 0,
};

// ky `api` has a prefixUrl, so the path must NOT start with a leading slash.
const tasksPath = (workspaceId: number) =>
  `type_curve_workspaces/${workspaceId}/tasks`;

const tasksKey = (workspaceId: number | null): QueryKey => [
  WORKSPACE_TASKS_KEY,
  workspaceId,
];

// Loads the workspace's verification checklist and mirrors it into
// tcWorkspaceTasksAtom so the sidebar panel and header badge can read it without
// React Query. Gated on the effective workspace id; a null id clears the mirror.
export function useWorkspaceTasksQuery() {
  const workspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  const setTasks = useSetAtom(tcWorkspaceTasksAtom);

  const query = useQuery<WorkspaceTasksResponse>({
    queryKey: tasksKey(workspaceId),
    queryFn: () =>
      api.get(tasksPath(workspaceId as number)).json<WorkspaceTasksResponse>(),
    enabled: !!workspaceId,
  });

  const { data } = query;
  useEffect(() => {
    // Clear when there is no workspace or while a new workspace's list loads,
    // so a stale workspace's tasks never linger in the sidebar.
    setTasks(workspaceId && data ? data : EMPTY_TASKS);
  }, [data, workspaceId, setTasks]);

  return query;
}

export interface SaveWorkspaceTaskVars {
  path: string;
  // true = correct, false = incorrect, null = revert to unverified.
  correctnessState: boolean | null;
  reason: string;
  // Cited analog-well filter ids (REPLACES the linked set on the backend).
  evidences?: number[];
  // Field-backed tasks: write the verified header-detail field in the same call.
  header?: { key: string; value: unknown };
  // Type Curve Volumes tasks: upsert per-stream DCA fit hyper-params, plus the
  // fit's segmentation / outlier month-online indices when present.
  dcaForecasts?: Array<{
    stream: string;
    dca_params: Record<string, unknown>;
    dca_scenario_id?: number;
    aggregation?: string;
    segment_months_online?: number[];
    outlier_months_online?: number[];
  }>;
}

// Best-effort human message from a task-save error (e.g. promoted workspace 422).
export async function parseTaskError(error: unknown): Promise<string | null> {
  if (!(error instanceof HTTPError)) {
    return error instanceof Error ? error.message : null;
  }
  const body = await error.response.json().catch(() => null);
  if (body && typeof body === 'object') {
    const obj = body as { message?: string; error?: { message?: string } };
    if (obj.message) return obj.message;
    if (obj.error?.message) return obj.error.message;
  }
  return error.message;
}

// Saves a single task's verdict + note via PATCH .../tasks/:path (the atomic
// verification save). On success the returned task replaces its entry in the
// list cache, which re-mirrors into the atom (updating the sidebar + badge).
export function useSaveWorkspaceTask() {
  const queryClient = useQueryClient();
  const workspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  const queryKey = tasksKey(workspaceId);

  return useMutation<WorkspaceTask, unknown, SaveWorkspaceTaskVars>({
    mutationFn: ({
      path,
      correctnessState,
      reason,
      evidences,
      header,
      dcaForecasts,
    }) => {
      if (!workspaceId) {
        return Promise.reject(new Error('No workspace to save task'));
      }
      // wrap_parameters is off on this endpoint — send bare snake_case keys.
      const body: Record<string, unknown> = {
        correctness_state: correctnessState,
        reason,
      };
      if (evidences !== undefined) body.evidences = evidences;
      if (header !== undefined) body.header = header;
      if (dcaForecasts !== undefined) body.dca_forecasts = dcaForecasts;
      return api
        .patch(`${tasksPath(workspaceId)}/${path}`, { json: body })
        .json<WorkspaceTask>();
    },
    onSuccess: (saved) => {
      queryClient.setQueryData<WorkspaceTasksResponse>(queryKey, (prev) => {
        if (!prev) return prev;
        const tasks = prev.tasks.map((t) => {
          return t.path === saved.path ? saved : t;
        });
        return {
          ...prev,
          tasks,
          completed: tasks.filter((t) => t.status !== null).length,
        };
      });
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}
